import * as React from 'react';
import { AppContext } from '../../../App';
import { DialogContentText, Tooltip, IconButton } from '@mui/material';
import useGlynt from '../../hooks/useGlynt';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import errorResponses from '../../../errorResponses';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@mui/material/styles';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';

export interface IPackageDeleteProps {
  packageId: string;
}

const PackageDelete: React.FC<IPackageDeleteProps> = ({ packageId }) => {
  const [open, setOpen] = React.useState(false);

  const { setToast } = React.useContext(AppContext);
  const glynt = useGlynt();
  const client = useQueryClient();
  const theme = useTheme();

  const deletePackageMutation = useMutation({
    mutationFn: async () => (await glynt).packages.delete(packageId),

    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['packages']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Package Deleted.',
      });
    },

    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToDeletePackage,
      });
    }
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  const handleDelete = () => {
    deletePackageMutation.mutate();
    setOpen(false);
  };

  return (
    <>
      <Tooltip placement="top" title={`Delete Package`} disableFocusListener disableTouchListener>
        <IconButton
          data-cy={`btn-delete-package-${packageId}`}
          onClick={!deletePackageMutation.isPending ? handleClickOpen : undefined}
          disabled={deletePackageMutation.isPending}
          sx={{
            color: theme.palette.custom.lightGreen,
            '&:hover': {
              backgroundColor: theme.palette.custom.lightGreen + '20', // 20% opacity for hover
            },
          }}
        >
          <DeleteIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <DialogGlynt
        open={open}
        closeModal={handleCloseConfirmation}
        loading={deletePackageMutation.isPending}
        cypressId="dialog-package-delete"
      >
        <DialogTitleGlynt
          title="Confirmation"
          closeModal={handleCloseConfirmation}
          loading={deletePackageMutation.isPending}
        />
        <DialogContentGlynt>
          <DialogContentText>Are you sure you want to delete this package?</DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleDelete}
          closeModal={handleCloseConfirmation}
          cancelCypressId="btn-package-delete-modal-cancel"
          submitCypressId="btn-package-delete-modal-yes"
          clearCypressId=""
          submitButtonText="Yes"
          submitLoading={deletePackageMutation.isPending}
          showClearButton={false}
        />
      </DialogGlynt>
    </>
  );
};

export default PackageDelete;
